import api from './api';

export interface UsagePeriod {
  startDate: string;
  endDate: string;
  month: number;
  year: number;
}

export interface MyUsageStats {
  success: boolean;
  period: UsagePeriod;
  ticketsOpened: number;
  byStatus: Record<string, number>;
  hoursLogged: number | null;
  hoursOnMyTickets: number;
  hourlyRate: number;
  currency: string;
  cost: number | null;
}

export interface EmployeeUsageRow {
  user: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
    status: string;
  };
  ticketsOpened: number;
  byStatus: Record<string, number>;
  billableHours: number;
  cost: number | null;
}

export interface CompanyEmployeeUsage {
  success: boolean;
  company: {
    _id: string;
    name: string;
    hourlyRate: number;
    currency: string;
  };
  period: UsagePeriod;
  employees: EmployeeUsageRow[];
  totals: {
    ticketsOpened: number;
    billableHours: number;
    cost: number | null;
  };
}

export type UsageQuery = {
  month?: number;
  year?: number;
  startDate?: string;
  endDate?: string;
  companyId?: string;
};

const toParams = (query: UsageQuery = {}) => {
  const params = new URLSearchParams();
  if (query.startDate && query.endDate) {
    params.append('startDate', query.startDate);
    params.append('endDate', query.endDate);
  } else {
    if (query.month) params.append('month', String(query.month));
    if (query.year) params.append('year', String(query.year));
  }
  if (query.companyId) params.append('companyId', query.companyId);
  return params;
};

export const timeTrackingService = {
  getAgentTimeStats: async (month?: number, year?: number) => {
    const params = new URLSearchParams();
    if (month) params.append('month', month.toString());
    if (year) params.append('year', year.toString());

    const response = await api.get(`/time-tracking/agents/stats?${params.toString()}`);
    return response.data;
  },

  getAgentDetailedStats: async (agentId: string, startDate?: string, endDate?: string) => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await api.get(`/time-tracking/agents/${agentId}/detailed?${params.toString()}`);
    return response.data;
  },

  getTicketTotalTime: async (ticketId: string) => {
    const response = await api.get(`/time-tracking/tickets/${ticketId}/total`);
    return response.data;
  },

  getAgentTimeOnTicket: async (ticketId: string, agentId: string) => {
    const response = await api.get(`/time-tracking/tickets/${ticketId}/agent/${agentId}`);
    return response.data;
  },

  getMyTimeOnTicket: async (ticketId: string) => {
    const response = await api.get(`/time-tracking/tickets/${ticketId}/my-time`);
    return response.data;
  },

  getMyUsageStats: async (query: UsageQuery = {}): Promise<MyUsageStats> => {
    const response = await api.get(`/time-tracking/me/stats?${toParams(query).toString()}`);
    return response.data;
  },

  getCompanyEmployeeUsage: async (query: UsageQuery = {}): Promise<CompanyEmployeeUsage> => {
    const response = await api.get(`/time-tracking/company/employees?${toParams(query).toString()}`);
    return response.data;
  }
};
